import React from 'react';
import { withTranslation } from 'react-i18next';
import { compose, mapProps } from 'recompose';
import { graphql } from 'react-apollo';
import Button from 'components/common/Buttons/Button';
import { withRouter } from 'react-router-dom';
import ThreeppModal from 'components/common/Modal/Modal';
import ThreeppSelect from 'components/common/Select/Select';
import MenuItem from '@material-ui/core/MenuItem';
import { getDomainRestrictedTemplates } from 'graphql/operations';
import { getDomainRestrictedTemplatesSelector, isFetchingDomainRestrictedTemplates } from 'selectors/templates/selector';
import { isEmptyString, sortObjectArrayByProperty } from 'utils/functions';

import './CreateNewModal.scss';

export const CreateNewModal = props => {
  const { t, history, toggle, isOpen, templateData } = props;

  // State management
  const [selectedCompany, setSelectedCompany] = React.useState('');
  const [selectedTemplate, setSelectedTemplate] = React.useState('');
  const [availableCompanies, setAvailableCompanies] = React.useState([]);
  const [availableTemplates, setAvailableTemplates] = React.useState([]);

  React.useEffect(() => {
    const templateCompanies = templateData.map(template => {
      const { company = t('newRecord.noCompany') } = template;
      return company;
    });
    // Only store unique company names
    const uniqueCompanies = [...new Set(templateCompanies)].filter(company => company).sort();
    setAvailableCompanies(uniqueCompanies);
  }, [isOpen, t, templateData]);

  React.useEffect(() => {
    const availableNewRecordTemplates = sortObjectArrayByProperty(
      templateData.filter(template => {
        return template.company === selectedCompany || (!template.company && selectedCompany === t('newRecord.noCompany'));
      }),
      'name'
    );
    setAvailableTemplates(availableNewRecordTemplates);
  }, [isOpen, templateData, selectedCompany, t]);

  React.useEffect(() => {
    // Clear curently selected options when user closes modal
    if (!isOpen) {
      setSelectedCompany('');
      setSelectedTemplate('');
      setAvailableCompanies([]);
      setAvailableTemplates([]);
    }
  }, [isOpen]);

  // Handlers
  const handleCompanySelection = event => {
    const companyName = event.target.value;
    setSelectedCompany(companyName);
  };
  const handleTemplateSelection = event => {
    const selectedTemplateUrlGuid = event.target.value;
    setSelectedTemplate(selectedTemplateUrlGuid);
  };
  const handleClose = () => {
    toggle();
  };
  const handleCreateTemplate = () => {
    handleClose();
    history.push(`/new/${selectedTemplate}`);
  };

  // DOM Generators
  const createCompanyOptions = () => {
    const dropdownOptions = [];
    availableCompanies.map(company =>
      dropdownOptions.push(
        <MenuItem value={company} key={company}>
          {company}
        </MenuItem>
      )
    );
    return dropdownOptions;
  };
  const createTemplateOptions = () => {
    const dropdownOptions = [];
    availableTemplates.map(template =>
      dropdownOptions.push(
        <MenuItem value={template.urlGuid} key={template.id}>
          {template.name}
        </MenuItem>
      )
    );
    return dropdownOptions;
  };

  return (
    <ThreeppModal isOpen={isOpen} toggle={toggle} classes="create-new-modal" header={t('newRecord.createNew')} size="sm">
      <>
        <ThreeppSelect variant="filled" label={t('newRecord.organization')} value={selectedCompany} onChange={handleCompanySelection}>
          {createCompanyOptions()}
        </ThreeppSelect>

        <ThreeppSelect
          variant="filled"
          label={t('newRecord.report')}
          value={selectedTemplate}
          onChange={handleTemplateSelection}
          disabled={!selectedCompany}
        >
          {createTemplateOptions()}
        </ThreeppSelect>

        <div className="action-row">
          <div className="btn-group">
            <Button className="btn-cancel" buttonStyle="text" onClick={handleClose}>
              {t('common.cancel')}
            </Button>

            <Button className="btn-create" buttonStyle="primary" onClick={handleCreateTemplate} disabled={isEmptyString(selectedTemplate)}>
              {t('common.create')}
            </Button>
          </div>
        </div>
      </>
    </ThreeppModal>
  );
};

export const CreateNewModalHOC = compose(
  withTranslation(),
  withRouter,
  graphql(getDomainRestrictedTemplates, { name: 'getDomainRestrictedTemplates' }),
  mapProps(props => ({
    t: props.t,
    history: props.history,
    toggle: props.toggle,
    isLoading: isFetchingDomainRestrictedTemplates(props),
    isOpen: props.isOpen,
    templateData: getDomainRestrictedTemplatesSelector(props)
  }))
);

export default CreateNewModalHOC(CreateNewModal);
